import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
  answerBlock
} from 'actions';

import Question from 'components/Questionnaires/Question';
import { QuestionTextField, QuestionJurisdictionDropDownField, QuestionIgaSubClassificationDropdownField, QuestionButtonsField } from 'components/Questionnaires/fields';
import { requiredValidator, validateQuestion, isBlockFullyValid } from 'components/Questionnaires/validators';
import { getClassificationsForCountry } from 'common/igaSubClassification/igaSubClassificationHelper';
import showQuestion from 'components/Questionnaires/questions';

export const QuestionNonReportingIgaBlock = ({ block, onAnswer }) => {
  const dispatch = useDispatch();

  const { componentProps } = block;
  const { sponsorTrusteeProps } = componentProps;
  const [answer, setAnswer] = useState(block.answers ?? {});
  const igaSubClassificationCombos = useSelector(state => state?.app?.staticData?.igaSubClassificationsCombos ?? []);
  const jurisdictions = useSelector(state => state?.app?.staticData?.jurisdictions ?? []);
  const qAnswers = useSelector(state => state?.questionnaires?.formQuestionnaire?.answers);

  const countryHasIgaSubClassifications = (jurisdiction) => {
    const jurisdictionsDictionary = new Map(jurisdictions.map(j => [j.countryCodeAlpha, j.isoJurisdictionName]));
    const jurisdictionName = jurisdictionsDictionary.get(jurisdiction);
    const subclassificationsForCountry = getClassificationsForCountry(jurisdictionName, igaSubClassificationCombos);

    return subclassificationsForCountry && subclassificationsForCountry.length > 0;
  };

  const [country, setCountry] = useState({
    id: 'country',
    label: 'questionnaire.ch4.nonReportingIgaFfi.country.label',
    component: QuestionJurisdictionDropDownField,
    componentProps: {
      annexIIOnly: true
    },
    default: 'questionnaire.fields.questionJurisdictionDropDown.nonReportingIgaFfi.default',
    visible: true,
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.ch4.nonReportingIgaFfi.country.errorMessage' }
      ]
    },
    answer: answer?.country
  });
  const [igaSubclassification, setSubclassification] = useState({
    id: 'igaSubclassification',
    label: 'questionnaire.ch4.nonReportingIgaFfi.igaSubclassification.label',
    component: QuestionIgaSubClassificationDropdownField,
    componentProps: {
      jurisdiction: ''
    },
    visible: false,
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.ch4.nonReportingIgaFfi.igaSubclassification.errorMessage' }
      ]
    },
    answer: answer?.igaSubclassification
  });
  const [sponsorTrusteeName, setSponsorTrusteeName] = useState({
    id: 'sponsorTrusteeName',
    component: QuestionTextField,
    label: 'questionnaire.ch4.nonReportingIgaFfi.sponsorTrusteeName.label',
    info: {
      instructions: 'questionnaire.ch4.nonReportingIgaFfi.sponsorTrusteeName.info',
    },
    visible: sponsorTrusteeProps?.visible,
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.ch4.nonReportingIgaFfi.sponsorTrusteeName.errorMessage' }
      ]
    },
    answer: answer?.sponsorTrusteeName
  });

  const [isUSTrustee, setIsUSTrustee] = useState({
    id: 'isUSTrustee',
    component: QuestionButtonsField,
    componentProps: {
      buttons: [
        { value: 'Yes', text: 'questionnaire.ch4.nonReportingIgaFfi.buttons.US' },
        { value: 'No', text: 'questionnaire.ch4.nonReportingIgaFfi.buttons.Foreign' }
      ]
    },
    label: 'questionnaire.ch4.nonReportingIgaFfi.isUSTrustee.label',
    visible: sponsorTrusteeProps?.visible,
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.ch4.nonReportingIgaFfi.isUSTrustee.errorMessage' }
      ]
    },
    answer: answer?.isUSTrustee
  });

  const blockIdentifier = useMemo(() => ((block && block.id) ? `${ block.id }${ block.index !== undefined ? (`.${ block.index }`) : '' }` : undefined), [block.index]);

  const isBlockValid = () => {
    const questions = [country, igaSubclassification, sponsorTrusteeName, isUSTrustee];
    return isBlockFullyValid(null, questions, block?.answers, qAnswers);
  };

  useEffect(() => {
    let isValid;
    if (!onAnswer || typeof onAnswer !== 'function') {
      dispatch(answerBlock(block.id, answer, isBlockValid()));
    } else {
      onAnswer(block, answer, isValid);
    }
  }, [answer]);

  useEffect(() => {
    setAnswer(block.answers ?? {});
  }, [block.answers]);

  const onQuestionChange = (q, value) => {
    switch (q.id) {
      case 'country': {
        const newCountry = { ...q, answer: value };
        validateQuestion(newCountry);
        setCountry(newCountry);
        setAnswer({ ...answer, country: value, igaSubclassification: undefined });

        const newSubclassification = { ...igaSubclassification, answer: undefined, isValid: undefined, errorMessages: undefined };
        newSubclassification.visible = countryHasIgaSubClassifications(value);
        newSubclassification.componentProps.jurisdiction = value;
        setSubclassification(newSubclassification);
        break;
      }
      case 'igaSubclassification': {
        const newSubclassification = { ...q, answer: value };
        validateQuestion(newSubclassification);
        setSubclassification(newSubclassification);
        setAnswer({ ...answer, igaSubclassification: value, sponsorTrusteeName: undefined, isUSTrustee: undefined });

        const newSponsorTrusteeName = { ...sponsorTrusteeName, answer: undefined, isValid: undefined, errorMessages: undefined };
        const newIsUSTrustee = { ...isUSTrustee, answer: undefined, isValid: undefined, errorMessages: undefined };
        setSponsorTrusteeName(newSponsorTrusteeName);
        setIsUSTrustee(newIsUSTrustee);
        break;
      }
      case 'sponsorTrusteeName': {
        const newSponsorTrusteeName = { ...q, answer: value };
        validateQuestion(newSponsorTrusteeName);
        setSponsorTrusteeName(newSponsorTrusteeName);
        setAnswer({ ...answer, sponsorTrusteeName: value });
        break;
      }
      case 'isUSTrustee': {
        const newIsUSTrustee = { ...q, answer: value };
        validateQuestion(newIsUSTrustee);
        setIsUSTrustee(newIsUSTrustee);
        setAnswer({ ...answer, isUSTrustee: value });
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    if (block.isForceEdited === true) {
      if (!answer?.country) {
        onQuestionChange({ ...country, isForceEdited: true }, '');
      }
      if (!answer?.igaSubclassification) {
        onQuestionChange({ ...igaSubclassification, isForceEdited: true }, '');
      }
      if (!answer?.sponsorTrusteeName) {
        onQuestionChange({ ...sponsorTrusteeName, isForceEdited: true }, '');
      }
      if (!answer?.isUSTrustee) {
        onQuestionChange({ ...isUSTrustee, isForceEdited: true }, '');
      }
    }

    if (block.isForceEdited === undefined) {
      if (country.isForceEdited === true) {
        setCountry({ ...country, isForceEdited: undefined });
      }
      if (igaSubclassification.isForceEdited === true) {
        setSubclassification({ ...igaSubclassification, isForceEdited: undefined });
      }
      if (sponsorTrusteeName.isForceEdited === true) {
        setSponsorTrusteeName({ ...sponsorTrusteeName, isForceEdited: undefined });
      }
      if (isUSTrustee.isForceEdited === true) {
        setIsUSTrustee({ ...isUSTrustee, isForceEdited: undefined });
      }
    }
  }, [block.isForceEdited]);

  return (<React.Fragment>
    <Question key={ country.id } blockId={ blockIdentifier } question={ country } { ...country.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    {showQuestion(igaSubclassification, block?.answers, qAnswers)
    && <Question key={ igaSubclassification.id } blockId={ blockIdentifier } question={ igaSubclassification } { ...igaSubclassification.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />}
    {showQuestion(sponsorTrusteeName, block?.answers, qAnswers)
    && <Question key={ sponsorTrusteeName.id } blockId={ blockIdentifier } question={ sponsorTrusteeName } { ...sponsorTrusteeName.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />}
    {showQuestion(isUSTrustee, block?.answers, qAnswers)
    && <Question key={ isUSTrustee.id } blockId={ blockIdentifier } question={ isUSTrustee } { ...isUSTrustee.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />}
  </React.Fragment>);
};

QuestionNonReportingIgaBlock.displayName = 'QuestionNonReportingIgaBlock';

QuestionNonReportingIgaBlock.propTypes = {
  block: PropTypes.shape().isRequired,
  onAnswer: PropTypes.func,
  preQualAnswers: PropTypes.shape(),
};

QuestionNonReportingIgaBlock.defaultProps = {
  onAnswer: null,
  preQualAnswers: null
};

export default QuestionNonReportingIgaBlock;
